// Shared confirm-action modal. Per spec/build-plan.md P-024 row decision
// surface ("modal for V1, clearer affordance, harder to misclick") +
// the QA item "Destructive actions (delete) show a confirm dialog;
// non-destructive (duplicate) don't."
//
// **Built on `<Modal>` (P-032).** The shell handles open/short-circuit,
// overlay click-to-cancel, ESC-key cancel, and panel chrome. This
// component layers the title + message + Cancel/Confirm footer, plus
// the auto-focus on Confirm. Per /simplify at P-032 close: the 3rd
// modal consumer triggered the scaffold extraction.

import { useEffect, useRef } from 'preact/hooks'

import { destructiveButtonClass, secondaryButtonClass } from './button-classes'
import { Modal } from './modal'

interface ConfirmModalProps {
  readonly open: boolean
  readonly title: string
  readonly message: string
  readonly confirmLabel?: string
  readonly cancelLabel?: string
  readonly onConfirm: () => void
  readonly onCancel: () => void
}

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  const confirmRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return
    confirmRef.current?.focus()
  }, [open])

  return (
    <Modal open={open} onCancel={onCancel} labelledBy="confirm-modal-title">
      <h2 id="confirm-modal-title" class="m-0 text-lg font-semibold text-gray-900">
        {title}
      </h2>
      <p class="m-0 mt-2 text-sm text-gray-700">{message}</p>
      <div class="mt-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          class={secondaryButtonClass}
        >
          {cancelLabel}
        </button>
        <button
          ref={confirmRef}
          type="button"
          onClick={onConfirm}
          class={destructiveButtonClass}
        >
          {confirmLabel}
        </button>
      </div>
    </Modal>
  )
}
